import { useMemo } from "react";
import { MRT_ColumnDef, MRT_RowData } from "material-react-table";
import { getColumnId } from "../common/TableUtils";

/**
 * Drops the named columns from a column set.
 *
 * Used by tables anchored to a single entity — an entity page's connection or
 * filtered sections — where a column repeats that entity on every row and so
 * carries no information. Columns are removed outright rather than toggled via
 * MRT's `columnVisibility`, which would leave them re-showable from the column
 * actions menu.
 *
 * The memo is keyed on the ids themselves rather than on the array identity, so
 * callers can pass an inline array without rebuilding the columns — and MRT
 * re-creating its column defs — on every render.
 */
const useVisibleColumns = <T extends MRT_RowData>(
  columns: MRT_ColumnDef<T>[],
  hiddenColumnIds?: string[]
): MRT_ColumnDef<T>[] => {
  const hiddenColumnsKey = hiddenColumnIds?.length
    ? [...hiddenColumnIds].sort().join("|")
    : "";

  return useMemo(() => {
    if (!hiddenColumnsKey) return columns;
    const hidden = new Set(hiddenColumnsKey.split("|"));
    return columns.filter((column) => {
      const id = getColumnId(column);
      // A column with no resolvable id cannot be the one that was asked for.
      return !id || !hidden.has(id);
    });
  }, [columns, hiddenColumnsKey]);
};

export default useVisibleColumns;
